import React, { useEffect, useRef, useState } from "react";
import { X, Camera, Aperture, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * LivePlateScanner
 *
 * Document-scanner style capture. Streams the rear camera into a <video>,
 * overlays a fixed guide-box (SA plates ~4:1), and on CAPTURE grabs ONLY the
 * pixels inside the box. That crop (typically 480×120 px @ q=0.65 → ~5-15 KB
 * base64) is passed back via onCapture(base64) — the surrounding photo is
 * NEVER stored, NEVER encoded, and never leaves the camera pipeline.
 */
export default function LivePlateScanner({ onCapture, onCancel }) {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [error, setError] = useState(null);
    const [starting, setStarting] = useState(true);
    const [autoStatus, setAutoStatus] = useState("waiting"); // waiting | steady | fired
    const stableFramesRef = useRef(0);
    const capturedRef = useRef(false);
    const rafRef = useRef(null);
    const prevPixelsRef = useRef(null);

    // Get camera and stream it
    useEffect(() => {
        let stream;
        (async () => {
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: { ideal: "environment" },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    },
                    audio: false,
                });
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play().catch(() => {});
                    setStarting(false);
                }
            } catch (e) {
                setError(
                    e?.name === "NotAllowedError"
                        ? "Camera permission was denied — enable it in your browser settings and try again."
                        : e?.name === "NotFoundError"
                        ? "No rear camera detected on this device."
                        : (e?.message || "Could not open camera.")
                );
                setStarting(false);
            }
        })();
        return () => {
            if (stream) {
                stream.getTracks().forEach((t) => t.stop());
            }
        };
    }, []);

    // Auto-capture: sample a tiny thumbnail of the guide-box every ~200ms.
    // When two consecutive samples are similar enough (mean-abs-diff < threshold)
    // for 3 in a row (~600ms), fire the capture automatically.
    useEffect(() => {
        if (error || starting) return undefined;
        let cancelled = false;
        const SAMPLE_W = 40;
        const SAMPLE_H = 12;
        const STABILITY_THRESHOLD = 8; // per-pixel mean-abs-diff (0-255)
        const NEEDED_STABLE_FRAMES = 3;
        const off = document.createElement("canvas");
        off.width = SAMPLE_W; off.height = SAMPLE_H;
        const octx = off.getContext("2d", { willReadFrequently: true });

        const tick = () => {
            if (cancelled || capturedRef.current) return;
            const video = videoRef.current;
            if (!video || video.videoWidth === 0) {
                rafRef.current = setTimeout(tick, 250);
                return;
            }
            // Same guide-box crop as capture() but shrunk to 40×12 for cheap diff
            const boxWidthPct = 0.80;
            const boxHeightPct = 0.22;
            const srcW = Math.round(video.videoWidth * boxWidthPct);
            const srcH = Math.round(video.videoHeight * boxHeightPct);
            const srcX = Math.round((video.videoWidth - srcW) / 2);
            const srcY = Math.round((video.videoHeight - srcH) / 2);
            try {
                octx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, SAMPLE_W, SAMPLE_H);
                const data = octx.getImageData(0, 0, SAMPLE_W, SAMPLE_H).data;
                if (prevPixelsRef.current) {
                    let diffSum = 0;
                    const prev = prevPixelsRef.current;
                    for (let i = 0; i < data.length; i += 4) {
                        diffSum += Math.abs(data[i] - prev[i]);
                    }
                    const meanDiff = diffSum / (SAMPLE_W * SAMPLE_H);
                    if (meanDiff < STABILITY_THRESHOLD) {
                        stableFramesRef.current += 1;
                        if (stableFramesRef.current >= NEEDED_STABLE_FRAMES) {
                            capturedRef.current = true;
                            setAutoStatus("fired");
                            capture();
                            return;
                        }
                        setAutoStatus("steady");
                    } else {
                        stableFramesRef.current = 0;
                        setAutoStatus("waiting");
                    }
                }
                prevPixelsRef.current = new Uint8ClampedArray(data);
            } catch {
                // ignore per-frame errors
            }
            rafRef.current = setTimeout(tick, 200);
        };
        rafRef.current = setTimeout(tick, 600); // give camera a beat
        return () => {
            cancelled = true;
            if (rafRef.current) clearTimeout(rafRef.current);
            off.width = 0; off.height = 0;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, starting]);

    const capture = () => {
        if (capturedRef.current && autoStatus !== "fired") return; // safety no-op
        capturedRef.current = true;
        const video = videoRef.current;
        if (!video || video.videoWidth === 0) {
            toast.error("Camera not ready — hold still and try again.");
            capturedRef.current = false;
            return;
        }
        // The guide box takes up ~80% of the width and ~20% of the height,
        // centered — extract exactly that region.
        const boxWidthPct = 0.80;
        const boxHeightPct = 0.22;
        const srcW = Math.round(video.videoWidth * boxWidthPct);
        const srcH = Math.round(video.videoHeight * boxHeightPct);
        const srcX = Math.round((video.videoWidth - srcW) / 2);
        const srcY = Math.round((video.videoHeight - srcH) / 2);

        // Target: max 720 wide (plates OCR fine well below this), preserve aspect
        const targetW = Math.min(720, srcW);
        const targetH = Math.round(srcH * (targetW / srcW));

        const canvas = canvasRef.current || document.createElement("canvas");
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, targetW, targetH);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
        // Free the canvas immediately to keep peak memory tiny
        canvas.width = 0;
        canvas.height = 0;
        const base64 = dataUrl.split(",")[1];
        onCapture(base64, "image/jpeg", dataUrl);
    };

    return (
        <div className="fixed inset-0 z-[70] bg-black flex flex-col" data-testid="live-plate-scanner">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-black/80 text-white">
                <div className="flex items-center gap-2">
                    <Camera size={18} weight="bold" />
                    <div>
                        <div className="text-xs uppercase tracking-widest text-slate-400">Plate scanner</div>
                        <div className="text-sm font-bold">Line up the plate inside the frame</div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onCancel}
                    className="p-2 rounded-full bg-slate-800/80 hover:bg-slate-700"
                    aria-label="Close scanner"
                    data-testid="live-scanner-close"
                >
                    <X size={20} weight="bold" />
                </button>
            </div>

            {/* Live video with the guide-box overlay */}
            <div className="relative flex-1 overflow-hidden bg-black">
                {error ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                        <div className="rounded-lg bg-rose-950/60 border border-rose-500/60 p-4 max-w-sm">
                            <div className="text-rose-200 font-bold mb-1">Camera unavailable</div>
                            <div className="text-rose-100/80 text-sm">{error}</div>
                        </div>
                    </div>
                ) : (
                    <>
                        <video
                            ref={videoRef}
                            playsInline
                            muted
                            autoPlay
                            className="w-full h-full object-cover"
                            data-testid="live-scanner-video"
                        />
                        {/* Guide-box overlay — a lit rectangle over a dimmed backdrop */}
                        <div className="absolute inset-0 pointer-events-none" data-testid="live-scanner-guide">
                            {/* Four dimmed strips forming a "cut-out" over the live plate area */}
                            <div className="absolute inset-x-0 top-0 h-[39%] bg-black/55" />
                            <div className="absolute inset-x-0 bottom-0 h-[39%] bg-black/55" />
                            <div className="absolute left-0 top-[39%] h-[22%] w-[10%] bg-black/55" />
                            <div className="absolute right-0 top-[39%] h-[22%] w-[10%] bg-black/55" />
                            {/* The lit guide rectangle with animated corners */}
                            <div className={`absolute left-[10%] top-[39%] w-[80%] h-[22%] border-2 rounded shadow-[0_0_0_2px_rgba(0,0,0,0.4)] transition-colors ${autoStatus === "steady" ? "border-yellow-400 animate-pulse" : autoStatus === "fired" ? "border-white bg-white/10" : "border-emerald-400/80"}`}>
                                <span className="absolute -top-1 -left-1 w-4 h-4 border-l-4 border-t-4 border-emerald-300" />
                                <span className="absolute -top-1 -right-1 w-4 h-4 border-r-4 border-t-4 border-emerald-300" />
                                <span className="absolute -bottom-1 -left-1 w-4 h-4 border-l-4 border-b-4 border-emerald-300" />
                                <span className="absolute -bottom-1 -right-1 w-4 h-4 border-r-4 border-b-4 border-emerald-300" />
                            </div>
                            {/* Helper text under the box — reflects auto-capture state */}
                            <div className="absolute inset-x-0 top-[63%] text-center text-white/85 text-xs font-bold tracking-wider" data-testid="live-scanner-status">
                                {autoStatus === "fired"
                                    ? "READING PLATE…"
                                    : autoStatus === "steady"
                                    ? "HOLD STEADY — CAPTURING…"
                                    : "POSITION THE PLATE INSIDE THE GREEN FRAME"}
                            </div>
                        </div>
                    </>
                )}
                {starting && (
                    <div className="absolute inset-0 flex items-center justify-center text-white/70">Starting camera…</div>
                )}
            </div>

            {/* Capture bar */}
            <div className="bg-black/85 py-4 flex items-center justify-center gap-6">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 rounded text-white/70 hover:text-white text-sm font-bold uppercase tracking-wider"
                    data-testid="live-scanner-cancel"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={capture}
                    disabled={!!error || starting}
                    className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:bg-slate-700 flex items-center justify-center border-4 border-white/90 shadow-lg transition-colors"
                    aria-label="Capture plate"
                    data-testid="live-scanner-capture"
                >
                    <Aperture size={28} weight="bold" className="text-white" />
                </button>
                <div className="w-16 flex items-center justify-center text-emerald-300">
                    <Check size={20} weight="bold" />
                </div>
            </div>
            <canvas ref={canvasRef} className="hidden" />
        </div>
    );
}
